import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { clearRoutes, setLoading, setHeaderLoading } from "../../store/slices/travelSlice";
import { ArrowLeftRight, Train, Plane, Bus, Plus, Minus, LayoutGrid } from 'lucide-react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TravelAutocompleteInput from "../search-form/TravelAutocompleteInput";
import { toast } from 'react-toastify';

const FormSearchSection = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Helper to get initial search data from localStorage
    const getInitialSearchData = () => {
        const savedSearch = localStorage.getItem("last_form_search");
        if (savedSearch) {
            try {
                return JSON.parse(savedSearch);
            } catch (e) {
                console.error("Error parsing saved search:", e);
            }
        }
        return null;
    };

    const initialData = getInitialSearchData();

    const [aiSearchInput, setAiSearchInput] = useState("");
    const [activeModes, setActiveModes] = useState(initialData?.activeModes || ['All']);
    const [showTravelersModal, setShowTravelersModal] = useState(false);
    const [passengerCounts, setPassengerCounts] = useState(initialData?.passengerCounts || {
        adults: 1,
        children: 0,
        infants: 0
    });
    const [seatClasses, setSeatClasses] = useState(initialData?.seatClasses || ['Economy', '3A']);
    const [travelDate, setTravelDate] = useState(() => {
        if (initialData?.travelDate) {
            const parsedDate = new Date(initialData.travelDate);
            if (parsedDate >= new Date().setHours(0, 0, 0, 0)) {
                return parsedDate;
            }
        }
        return null;
    });
    const [fromLocation, setFromLocation] = useState(initialData?.fromLocation || "");
    const [toLocation, setToLocation] = useState(initialData?.toLocation || "");
    const [fromLocationData, setFromLocationData] = useState(initialData?.fromLocationData || null);
    const [toLocationData, setToLocationData] = useState(initialData?.toLocationData || null);
    const [nonStopOnly, setNonStopOnly] = useState(initialData?.nonStopOnly ?? false);

    const travelersRef = React.useRef(null);

    React.useEffect(() => {
        // Sync travelers and seat classes when mode changes
        // Keep initial values if we just loaded from storage
        const isFirstRender = !aiSearchInput && fromLocation === (initialData?.fromLocation || "");
        if (!isFirstRender) {
             // Only reset if it's a manual user change, not the initial load
             // NOTE: Usually it's better to keep the modes sync logic separate
        }
    }, [activeModes]);

    // Persistent Search: No longer needed in useEffect as we use lazy init
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (travelersRef.current && !travelersRef.current.contains(event.target)) {
                setShowTravelersModal(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Toggle Mode Logic
    const toggleMode = (mode) => {
        if (mode === 'All') {
            setActiveModes(['All']);
            return;
        }

        setActiveModes(prev => {
            let newModes = [...prev];

            // If currently All is selected, clear it and start with the new mode
            if (newModes.includes('All')) {
                newModes = [];
            }

            if (newModes.includes(mode)) {
                // Deselect logic: prevent deselecting the last mode (optional, but good UX)
                // or replicate "allow empty" which defaults to something? 
                // User requirement: "all else can be selected independently"
                newModes = newModes.filter(m => m !== mode);
            } else {
                newModes.push(mode);
            }

            // If we ended up empty, default back to 'All' or just leave it?
            // "only ALL when selected no other can be left selected"
            // Let's enforce at least one selection? Or default to All if empty.
            if (newModes.length === 0) return ['All'];

            return newModes;
        });
    };

    // Toggle seat class selection (multi-select)
    const toggleSeatClass = (cls) => {
        setSeatClasses(prev => {
            const current = Array.isArray(prev) ? prev : [];
            const isTrainClass = ['1A', '2A', '3A', 'SL'].includes(cls);
            const isFlightClass = ['Economy', 'Business', 'First'].includes(cls);

            if (isTrainClass) {
                const withoutTrain = current.filter(c => !['1A', '2A', '3A', 'SL'].includes(c));
                return [...withoutTrain, cls];
            }
            if (isFlightClass) {
                const withoutFlight = current.filter(c => !['Economy', 'Business', 'First'].includes(c));
                return [...withoutFlight, cls];
            }
            return current;
        });
    };

    const isFormValid = fromLocation && toLocation && travelDate;

    const handleAISearch = (e) => {
        e.preventDefault();
        navigate("/chat", { state: { fromHero: true, heroInput: aiSearchInput } });
    };

    return (
        <section className="home-search-section">
            {/* AI Search Bar - Always Visible */}
            <div className="search-bar" style={{ margin: 0 }}>
                <form onSubmit={handleAISearch} style={{ display: "flex", width: "100%", alignItems: "center" }}>
                    <div className="search-input" style={{ flex: 1 }}>
                        <img src="/Star1.svg" alt="AI" />
                        <input
                            type="text"
                            value={aiSearchInput}
                            onChange={e => setAiSearchInput(e.target.value)}
                            placeholder="Darbhanga se Mumbai jane ke liye kal ki date ke liye routes batao"
                            style={{
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                width: '100%'
                            }}
                        />
                    </div>
                    <button className="search-button" type="submit">
                        <img src="/send.svg" alt="Send" />
                    </button>
                </form>
            </div>

            {/* Divider */}
            <div className="form-divider-or">
                <div className="divider-line"></div>
                OR
                <div className="divider-line"></div>
            </div>

            {/* Advanced Form Search */}
            <div className="form-search-container">
                <div className="home-search-bar-wrapper" style={{ width: '100%', position: 'relative' }}>
                    <div className="home-search-bar">
                        <div className="search-field home-search-main-field">
                            <TravelAutocompleteInput
                                className="search-field-input"
                                placeholder="From"
                                value={fromLocation}
                                onChange={(val) => {
                                    setFromLocation(val);
                                    setFromLocationData(null);
                                }}
                                onSelect={(item) => {
                                    setFromLocation(item.label);
                                    setFromLocationData(item);
                                }}
                            />
                            <div className="swap-btn" onClick={() => {
                                const temp = fromLocation;
                                const tempData = fromLocationData;
                                setFromLocation(toLocation);
                                setFromLocationData(toLocationData);
                                setToLocation(temp);
                                setToLocationData(tempData);
                            }}>
                                <ArrowLeftRight size={20} />
                            </div>
                            <TravelAutocompleteInput
                                className="search-field-input"
                                placeholder="To"
                                value={toLocation}
                                onChange={(val) => {
                                    setToLocation(val);
                                    setToLocationData(null);
                                }}
                                onSelect={(item) => {
                                    setToLocation(item.label);
                                    setToLocationData(item);
                                }}
                            />
                        </div>

                        <div className="search-field-sep"></div>

                        <div className="search-field date-picker-container">
                            <DatePicker
                                selected={travelDate}
                                onChange={(date) => setTravelDate(date)}
                                dateFormat="MMM dd/eee"
                                className="search-field-input"
                                placeholderText="Select date"
                                minDate={new Date()}
                                isClearable={false}
                            />
                        </div>

                        <div className="search-field-sep"></div>

                        <div
                            className="search-field"
                            ref={travelersRef}
                            onClick={() => setShowTravelersModal(!showTravelersModal)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="search-field-input">
                                {`${passengerCounts.adults} adult, ${seatClasses.filter(c => {
                                    if (['1A', '2A', '3A', 'SL'].includes(c)) return activeModes.includes('Train') || activeModes.includes('All');
                                    if (['Economy', 'Business', 'First'].includes(c)) return activeModes.includes('Flight') || activeModes.includes('All');
                                    return true;
                                }).join(', ')}`}
                            </div>

                            {showTravelersModal && (
                                <div className="travelers-dropdown" onClick={(e) => e.stopPropagation()}>
                                    <h2 className="modal-title">Travelers</h2>
                                    {[
                                        { key: 'adults', label: 'Adults', age: '18+' },
                                        { key: 'children', label: 'Children', age: '2-11' },
                                        { key: 'infants', label: 'Infants', age: 'under 2' }
                                    ].map(({ key, label, age }) => (
                                        <div className="traveler-type-row" key={key}>
                                            <div className="type-info">
                                                <div className="label-with-age">
                                                    <h4>{label}</h4>
                                                    <span>{age}</span>
                                                </div>
                                            </div>
                                            <div className="counter-btns">
                                                <button
                                                    className="count-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPassengerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }));
                                                    }}
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span>{passengerCounts[key] || 0}</span>
                                                <button
                                                    className="count-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPassengerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }));
                                                    }}
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Seat Classes Section */}
                                    {(activeModes.includes('Flight') || activeModes.includes('All')) && (
                                        <div className="seat-classes-section">
                                            <h2 className="modal-title" style={{ fontSize: '20px', marginTop: '20px' }}>
                                                Flight Seat Classes
                                            </h2>
                                            <div className="seat-classes-grid">
                                                {['Economy', 'Business', 'First'].map(cls => (
                                                    <div
                                                        key={cls}
                                                        className={`class-tag ${seatClasses.includes(cls) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSeatClass(cls);
                                                        }}
                                                    >
                                                        {cls}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {(activeModes.includes('Train') || activeModes.includes('All')) && (
                                        <div className="seat-classes-section">
                                            <h2 className="modal-title" style={{ fontSize: '20px', marginTop: '20px' }}>
                                                Train Seat Classes
                                            </h2>
                                            <div className="seat-classes-grid">
                                                {['1A', '2A', '3A', 'SL'].map(cls => (
                                                    <div
                                                        key={cls}
                                                        className={`class-tag ${seatClasses.includes(cls) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSeatClass(cls);
                                                        }}
                                                    >
                                                        {cls}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="modal-actions">
                                        <button className="modal-clear-btn" onClick={(e) => {
                                            e.stopPropagation();
                                            setPassengerCounts({ adults: 1, children: 0, infants: 0 });
                                            // Reset seat classes based on current modes
                                            const defaults = [];
                                            if (activeModes.includes('Train') || activeModes.includes('All')) defaults.push('3A');
                                            if (activeModes.includes('Flight') || activeModes.includes('All')) defaults.push('Economy');
                                            setSeatClasses(defaults.length > 0 ? defaults : ['Economy', '3A']);
                                        }}>Clear</button>
                                        <button className="apply-btn" onClick={(e) => {
                                            e.stopPropagation();
                                            setShowTravelersModal(false);
                                        }}>Apply</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modes-container">
                    <span>Preferred modes</span>
                    <div className="modes-tabs">
                        <button className={`mode-btn ${activeModes.includes('All') ? 'active' : ''}`} onClick={() => toggleMode('All')}>
                            <LayoutGrid size={20} /> All
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Train') ? 'active' : ''}`} onClick={() => toggleMode('Train')}>
                            <Train size={20} /> Train
                        </button>
                        <button className={`mode-btn ${activeModes.includes('Flight') ? 'active' : ''}`} onClick={() => toggleMode('Flight')}>
                            <Plane size={20} /> Flight
                        </button>
                        {/* <button className={`mode-btn ${activeModes.includes('Bus') ? 'active' : ''}`} onClick={() => toggleMode('Bus')}>
                            <Bus size={20} /> Bus
                        </button> */}
                    </div>

                    <div className="options-row">
                        <label className="non-stop">
                            <input
                                type="checkbox"
                                checked={nonStopOnly}
                                onChange={(e) => setNonStopOnly(e.target.checked)}
                            />
                            <span>Non stop only</span>
                        </label>
                    </div>
                    <button
                        className={`primary-search-btn ${!isFormValid ? 'disabled' : ''}`}
                        disabled={!isFormValid}
                        onClick={() => {
                            if (!fromLocation || String(fromLocation).trim() === '') {
                                toast.error('Please enter departure city', { position: 'top-center' });
                                return;
                            }
                            if (!toLocation || String(toLocation).trim() === '') {
                                toast.error('Please enter destination city', { position: 'top-center' });
                                return;
                            }
                            if (!travelDate) {
                                toast.error('Please select travel date', { position: 'top-center' });
                                return;
                            }

                            if (String(fromLocation).trim().toLowerCase() === String(toLocation).trim().toLowerCase()) {
                                toast.error('Origin and Destination cannot be the same', { position: 'top-center' });
                                return;
                            }

                            // Advanced check: Same city but different station/airport names
                            if (fromLocationData && toLocationData) {
                                const normalizeCity = (c) => {
                                    const city = (c || '').trim().toLowerCase();
                                    if (city === 'new delhi') return 'delhi';
                                    return city;
                                };

                                const originCity = normalizeCity(fromLocationData.city);
                                const destCity = normalizeCity(toLocationData.city);
                                const originCode = (fromLocationData.code || '').trim().toLowerCase();
                                const destCode = (toLocationData.code || '').trim().toLowerCase();

                                // If city names match OR airport/station codes match, it's the same location effectively
                                if ((originCity && destCity && originCity === destCity) ||
                                    (originCode && destCode && originCode === destCode)) {
                                    toast.error('Origin and Destination cannot be in the same city', { position: 'top-center' });
                                    return;
                                }
                            }

                            dispatch(clearRoutes());
                            dispatch(setLoading(true));
                            dispatch(setHeaderLoading(true));

                            const params = new URLSearchParams({
                                from: fromLocation,
                                to: toLocation,
                                date: travelDate ? (() => {
                                    const d = new Date(travelDate);
                                    const year = d.getFullYear();
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const day = String(d.getDate()).padStart(2, '0');
                                    return `${year}-${month}-${day}`;
                                })() : '',
                                adults: passengerCounts.adults,
                                children: passengerCounts.children,
                                infants: passengerCounts.infants,
                                seatClasses: seatClasses.join(','),
                                modes: activeModes.join(','),
                                nonstop: nonStopOnly ? '1' : '0'
                            });
                            navigate(`/search-form?${params.toString()}`, {
                                state: {
                                    fromLocation,
                                    toLocation,
                                    fromLocationData,
                                    toLocationData,
                                    travelDate: travelDate ? travelDate.toISOString() : null,
                                    passengerCounts,
                                    seatClasses,
                                    activeModes,
                                    nonStopOnly: nonStopOnly
                                }
                            });

                            // Save search data to localStorage for persistence
                            localStorage.setItem("last_form_search", JSON.stringify({
                                fromLocation,
                                toLocation,
                                fromLocationData,
                                toLocationData,
                                travelDate: travelDate ? travelDate.toISOString() : null,
                                passengerCounts,
                                seatClasses,
                                activeModes,
                                nonStopOnly
                            }));
                        }}
                    >
                        Search Routes
                    </button>
                </div>
            </div>

        </section>
    );
};

export default FormSearchSection;
